import { APP_INITIALIZER, ApplicationConfig } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideLucideConfig } from '@lucide/angular';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from './core/auth/auth.interceptor';
import { AuthService } from './core/auth/auth.service';

/**
 * Application configuration for the Angular app. It sets up the router with the defined routes and
 * configures the HTTP client to use the authInterceptor for attaching authentication tokens to
 * outgoing requests.
 *
 * Additionally, it includes an APP_INITIALIZER provider that attempts to restore the user's session
 * by calling the tryRestoreSession method of the AuthService. This ensures that if the user has a
 * valid session (e.g., a valid refresh token in a cookie), it will be restored before the app
 * finishes initialisation. If the session restoration fails (e.g., due to an invalid or expired
 * refresh token), the app will proceed without an authenticated session, and the user will be
 * required to log in.
 *
 * @author sadatmalik
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideLucideConfig({
      size: 20,
      strokeWidth: 2
    }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService],
      useFactory: (auth: AuthService) => () => firstValueFrom(auth.tryRestoreSession())
    }
  ]
};
